import { createContext, useContext, useEffect, useState, useCallback, type Context } from 'react'
import { StorageKeys } from '@/lib/storage'
import { fetchWithAuth } from '@/lib/api'
import { isAbortFetchError } from '@/lib/api/core'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => Promise<void>
}

// Survive Vite HMR: without a stable singleton, Fast Refresh can create a second
// ThemeContext module instance so Provider and useTheme no longer share state
// ("useTheme must be used within ThemeProvider" while ThemeProvider is mounted).
const THEME_CONTEXT_KEY = '__flownotesThemeContext'
type ThemeContextGlobal = typeof globalThis & {
  [THEME_CONTEXT_KEY]?: Context<ThemeContextValue | undefined>
}
const themeGlobals = globalThis as ThemeContextGlobal
const ThemeContext =
  themeGlobals[THEME_CONTEXT_KEY] ?? createContext<ThemeContextValue | undefined>(undefined)
themeGlobals[THEME_CONTEXT_KEY] = ThemeContext

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('system')
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light')

  // Get system preference - simple, no caching workarounds
  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }, [])

  // Resolve theme (system -> light/dark)
  const resolveThemeValue = useCallback((themeValue: Theme): 'light' | 'dark' => {
    if (themeValue === 'system') {
      return getSystemTheme()
    }
    return themeValue
  }, [getSystemTheme])

  // Apply theme class to document root
  const applyTheme = useCallback((themeToApply: 'light' | 'dark', themeValue?: Theme) => {
    const root = document.documentElement
    if (themeToApply === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    setResolvedTheme(themeToApply)

    // Save to localStorage for FOUT prevention
    if (themeValue !== undefined) {
      try {
        localStorage.setItem(StorageKeys.THEME, themeValue)
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [])

  // Load theme from settings on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const response = await fetchWithAuth(`/api/settings`)
        if (!response.ok) {
          const localTheme = localStorage.getItem(StorageKeys.THEME) as Theme | null
          if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
            setThemeState(localTheme)
            applyTheme(resolveThemeValue(localTheme), localTheme)
          } else {
            const systemTheme = getSystemTheme()
            setThemeState('system')
            applyTheme(systemTheme, 'system')
          }
          return
        }
        const data = await response.json()
        if (data.success && data.settings?.ui?.theme) {
          const savedTheme = data.settings.ui.theme as Theme
          setThemeState(savedTheme)
          applyTheme(resolveThemeValue(savedTheme), savedTheme)
        } else {
          // Try localStorage as fallback
          const localTheme = localStorage.getItem(StorageKeys.THEME) as Theme | null
          if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
            setThemeState(localTheme)
            applyTheme(resolveThemeValue(localTheme), localTheme)
          } else {
            // Default to system
            const systemTheme = getSystemTheme()
            setThemeState('system')
            applyTheme(systemTheme, 'system')
          }
        }
      } catch (error) {
        // AbortError (nav-lock contention on reload, tab close) - fall back quietly, not an outage.
        if (!isAbortFetchError(error)) {
          console.error('[Theme] Failed to load theme:', error)
        }
        // Fallback to localStorage or system
        const localTheme = localStorage.getItem(StorageKeys.THEME) as Theme | null
        if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
          setThemeState(localTheme)
          applyTheme(resolveThemeValue(localTheme), localTheme)
        } else {
          const systemTheme = getSystemTheme()
          setThemeState('system')
          applyTheme(systemTheme, 'system')
        }
      }
    }

    loadTheme()
  }, [applyTheme, resolveThemeValue, getSystemTheme])

  // Update theme when theme value changes
  useEffect(() => {
    if (theme === 'system') {
      applyTheme(getSystemTheme(), 'system')
    } else {
      applyTheme(theme, theme)
    }
  }, [theme, applyTheme, getSystemTheme])

  // Listen to system preference changes (only when theme === 'system')
  useEffect(() => {
    if (theme !== 'system') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const syncFromMedia = (matches?: boolean) => {
      const newSystemTheme =
        matches !== undefined ? (matches ? 'dark' : 'light') : getSystemTheme()
      applyTheme(newSystemTheme, 'system')
    }

    const handleChange = (event: MediaQueryListEvent) => {
      syncFromMedia(event.matches)
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncFromMedia()
      }
    }

    const handleFocus = () => {
      syncFromMedia()
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      mediaQuery.addListener(handleChange as (e: MediaQueryListEvent) => void)
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleFocus)

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        mediaQuery.removeListener(handleChange as (e: MediaQueryListEvent) => void)
      }
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
    }
  }, [theme, applyTheme, getSystemTheme])

  // Update theme (called from settings)
  const updateTheme = useCallback(async (newTheme: Theme) => {
    setThemeState(newTheme)
    applyTheme(resolveThemeValue(newTheme), newTheme)

    // Save to settings API
    try {
      const getResponse = await fetchWithAuth(`/api/settings`)
      const getData = await getResponse.json()

      if (!getData.success) {
        console.error('[Theme] Failed to load current settings for update')
        return
      }

      const currentSettings = getData.settings || {}
      const updatedSettings = {
        ...currentSettings,
        ui: {
          ...currentSettings.ui,
          theme: newTheme
        }
      }

      const response = await fetchWithAuth(`/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: updatedSettings
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[Theme] Failed to save theme preference:', errorData.error || 'Unknown error')
      }
    } catch (error) {
      console.error('[Theme] Failed to save theme preference:', error)
    }
  }, [applyTheme, resolveThemeValue])

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme: updateTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

